import React from 'react';
import { FormattedMessage } from 'react-intl';

import { messages } from './messages';

type FTUWizardStepState = {
  hasStores?: boolean;
  hasShipFroms?: boolean;
  hasWallet?: boolean;
  storeCount?: number;
  shipFromCount?: number;
  isFormValid?: boolean;
  saveFunction?: (() => void) | null;
  latestImport?: unknown;
  initImport?: (onSuccess: () => void) => void;
  open?: () => void;
  updateConfig?: () => Promise<void>;
  hasRequiredData?: boolean;
  inventorySubstep?: string;
  updateInventorySubstep?: (substep: string) => void;
  inventoryManagementOption?: string | null;
  confirmInventoryManagementOption?: () => void;
  selectedConnections?: Array<{ connectionId: string; terminusType?: string }>;
  hasManualOrder?: boolean;
};

export const FTU_WIZARD_STEPS = {
  ORDERS: 'orders',
  SHIP_FROM: 'ship-from',
  CARRIER: 'carrier',
  PHONE_NUMBER: 'phone-number',
  DISCOUNTED_RATES: 'discounted-rates',
  ADVANCED_OPTIONS: 'advanced-options',
  INVENTORY_SETUP: 'inventory-setup',
  USER_WITHOUT_INVENTORY: 'user-without-inventory',
  RETURNS: 'returns',
};

export const NAVIGATION_ACTIONS = {
  PROCEED: 'proceed',
  STAY: 'stay',
  ERROR: 'error',
};

export const VIEW_TYPES = {
  CONFIGURED: 'configured',
  SETUP: 'setup',
  FINAL: 'final',
};

export const FTU_WIZARD_STEP_DISPLAY_ORDER = [
  FTU_WIZARD_STEPS.ORDERS,
  FTU_WIZARD_STEPS.SHIP_FROM,
  FTU_WIZARD_STEPS.CARRIER,
  FTU_WIZARD_STEPS.DISCOUNTED_RATES,
  FTU_WIZARD_STEPS.PHONE_NUMBER,
  FTU_WIZARD_STEPS.INVENTORY_SETUP,
  FTU_WIZARD_STEPS.RETURNS,
  FTU_WIZARD_STEPS.ADVANCED_OPTIONS,
];

export const FTU_WIZARD_STEP_TO_STEPPER_MAPPING = {
  [FTU_WIZARD_STEPS.ORDERS]: 1,
  [FTU_WIZARD_STEPS.SHIP_FROM]: 2,
  [FTU_WIZARD_STEPS.CARRIER]: 3,
  [FTU_WIZARD_STEPS.DISCOUNTED_RATES]: 3,
  [FTU_WIZARD_STEPS.PHONE_NUMBER]: 4,
  [FTU_WIZARD_STEPS.INVENTORY_SETUP]: 5,
  [FTU_WIZARD_STEPS.RETURNS]: 6,
  [FTU_WIZARD_STEPS.ADVANCED_OPTIONS]: 6,
};

export const STEPPER_LABELS = ['Orders', 'Ship From', 'Payments', 'Phone', 'Inventory', 'Returns'];

export const FTU_WIZARD_STEP_MESSAGE_IDS = {
  [FTU_WIZARD_STEPS.ORDERS]: 'stepOrders',
  [FTU_WIZARD_STEPS.SHIP_FROM]: 'stepShipping',
  [FTU_WIZARD_STEPS.CARRIER]: 'stepDiscountedRates',
  [FTU_WIZARD_STEPS.DISCOUNTED_RATES]: 'stepDiscountedRates',
  [FTU_WIZARD_STEPS.PHONE_NUMBER]: 'stepPhoneNumber',
  [FTU_WIZARD_STEPS.ADVANCED_OPTIONS]: 'stepAdvancedOptions',
  [FTU_WIZARD_STEPS.INVENTORY_SETUP]: 'stepInventory',
  [FTU_WIZARD_STEPS.RETURNS]: 'stepReturns',
};

export const FTU_WIZARD_STEP_CONFIGS = {
  [FTU_WIZARD_STEPS.ORDERS]: {
    showContinueButton: true,
    showSkipButton: (stepState: FTUWizardStepState, viewType: string) => {
      if (stepState.hasManualOrder) {
        return false;
      }
      return viewType !== VIEW_TYPES.CONFIGURED;
    },
    skipButtonText: <FormattedMessage {...messages.addOrdersLaterButtonText} />,
  },
  [FTU_WIZARD_STEPS.SHIP_FROM]: {
    showContinueButton: () => {
      return true;
    },
    showSkipButton: (_stepState: FTUWizardStepState, viewType: string) => {
      return viewType === VIEW_TYPES.SETUP;
    },
  },
  [FTU_WIZARD_STEPS.CARRIER]: {
    showContinueButton: (_stepState: FTUWizardStepState, viewType: string) => viewType === VIEW_TYPES.CONFIGURED,
    showSkipButton: (_stepState: FTUWizardStepState, viewType: string) => viewType !== VIEW_TYPES.CONFIGURED,
  },
  [FTU_WIZARD_STEPS.PHONE_NUMBER]: {
    showContinueButton: true,
    showSkipButton: true,
  },
  [FTU_WIZARD_STEPS.DISCOUNTED_RATES]: {
    showContinueButton: true,
    showSkipButton: false,
  },
  [FTU_WIZARD_STEPS.ADVANCED_OPTIONS]: {
    showContinueButton: true,
    showSkipButton: false,
  },
  [FTU_WIZARD_STEPS.INVENTORY_SETUP]: {
    showContinueButton: (_stepState: FTUWizardStepState, viewType: string) => {
      if (viewType === VIEW_TYPES.SETUP) return true;
      if (viewType === VIEW_TYPES.CONFIGURED) return true;
      if (viewType === VIEW_TYPES.FINAL) return true;
      return true;
    },
    showSkipButton: (_stepState: FTUWizardStepState, viewType: string) => {
      if (viewType === VIEW_TYPES.SETUP) return true;
      if (viewType === VIEW_TYPES.CONFIGURED) return false;
      if (viewType === VIEW_TYPES.FINAL) return true;
      return true;
    },
  },
  [FTU_WIZARD_STEPS.RETURNS]: {
    showContinueButton: true,
    showSkipButton: true,
  },
};

// Tracking event constants
export const FTU_SHIP_FROM_SAVE = 'FTU Ship From Save';
export const FTU_WIZARD_PAGE = 'FTU wizard';
